// HeroSectionElements.jsx - Updated with comprehensive responsive styles
import styled, { keyframes } from "styled-components";
import { Link as LinkScroll } from "react-scroll";

const pulse = keyframes`
  0% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.1); }
  100% { opacity: 0.3; transform: scale(1); }
`;

export const HeroContainer = styled.div`
  background: #0c0c0c;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 20px;
  min-height: 100vh;
  position: relative;
  z-index: 1;
  overflow: hidden;

  @media screen and (max-width: 768px) {
    padding: 60px 16px;
    min-height: 90vh;
  }

  @media screen and (max-width: 480px) {
    padding: 40px 12px;
    min-height: 85vh;
  }
`;

export const HeroBg = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

export const ImgBg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #232a34;
  filter: brightness(0.6) blur(2px);
  transform: scale(1.1);

  @media screen and (max-width: 768px) {
    filter: brightness(0.5) blur(1px);
  }
`;

export const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    108deg,
    rgba(0, 0, 0, 0.95) 0%,
    rgba(0, 0, 0, 0.7) 50%,
    rgba(0, 0, 0, 0.95) 100%
  );
  z-index: 2;

  @media screen and (max-width: 768px) {
    background: linear-gradient(
      108deg,
      rgba(0, 0, 0, 0.97) 0%,
      rgba(0, 0, 0, 0.8) 50%,
      rgba(0, 0, 0, 0.97) 100%
    );
  }
`;

export const HeroContent = styled.div`
  z-index: 3;
  max-width: 1200px;
  width: 100%;
  padding: 50px 24px; 
  display: flex;
  flex-direction: column; 
  align-items: center;
  justify-content: center; 
  text-align: center;

  @media screen and (max-width: 768px) {
    padding: 0 16px;
  }

  @media screen and (max-width: 480px) {
    padding: 0 12px;
  }
`;

export const HeroH1 = styled.h1`
  color: #fff;
  font-size: clamp(2rem, 6vw, 4rem);
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 24px;
  text-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  max-width: 900px;

  @media screen and (max-width: 768px) {
    font-size: clamp(1.5rem, 5vw, 2.5rem);
    margin-bottom: 20px;
    line-height: 1.3;
  }

  @media screen and (max-width: 480px) {
    font-size: clamp(1.3rem, 4vw, 2rem);
    margin-bottom: 16px;
  }

  br {
    @media screen and (max-width: 480px) {
      display: none;
    }
  }
`;

export const HeroP = styled.p`
  margin-top: 16px;
  color: rgba(255, 255, 255, 0.85);
  font-size: clamp(0.9rem, 2.5vw, 1.25rem);
  max-width: 700px;
  line-height: 1.6;
  letter-spacing: 0.02em;
  padding: 0 16px;

  @media screen and (max-width: 768px) {
    font-size: clamp(0.85rem, 2vw, 1rem);
    margin-top: 12px;
    line-height: 1.5;
    padding: 0 12px;
  }

  @media screen and (max-width: 480px) {
    font-size: 0.9rem;
    margin-top: 10px;
  }
`;

export const HeroBtnWrapper = styled.div`
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media screen and (max-width: 768px) {
    margin-top: 32px;
  }

  @media screen and (max-width: 480px) {
    margin-top: 28px;
  }
`;

export const ArrowForward = styled.span`
  margin-left: 8px;
  font-size: 20px;

  @media screen and (max-width: 768px) {
    font-size: 18px;
  }
`;

export const ArrowRight = styled.span`
  margin-left: 8px;
  font-size: 20px;

  @media screen and (max-width: 768px) {
    font-size: 18px;
  }
`;

export const FloatingShape = styled.div`
  position: absolute;
  z-index: 2;
  pointer-events: none;

  @media screen and (max-width: 768px) {
    display: none; /* Hide floating shapes on mobile for better performance */
  }
`;

export const GlowingOrb = styled.div`
  width: ${(props) => props.size || "100px"};
  height: ${(props) => props.size || "100px"};
  border-radius: 50%;
  background: ${(props) => props.color || "#4f46e5"};
  filter: blur(${(props) => props.blur || "40px"});
  opacity: 0.3;
  animation: ${pulse} 4s infinite;

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

export const CodeSnippet = styled.div`
  position: relative;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(12px);
  border-radius: 12px;
  padding: 20px 24px;
  font-family: "Fira Code", "Courier New", monospace;
  font-size: 14px;
  color: #e5e5e5;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  z-index: 3;
  text-align: left;
  width: 100%;
  max-width: 600px;
  margin: 32px auto 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  pre {
    margin: 0;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-size: inherit;

    code {
      color: #e5e5e5;
      font-family: inherit;
      line-height: 1.6;

      .cursor {
        color: #4f46e5;
        font-weight: bold;
        display: inline-block;
        width: 2px;
      }
    }
  }

  @media screen and (max-width: 768px) {
    width: 100%;
    max-width: 90%;
    font-size: 11px;
    padding: 16px 20px;
    margin: 24px auto 0;
    backdrop-filter: blur(8px);

    pre {
      code {
        line-height: 1.5;
        font-size: 11px;
      }
    }
  }

  @media screen and (max-width: 480px) {
    padding: 14px 16px;
    font-size: 10px;
    max-width: 95%;
    margin: 20px auto 0;

    pre code {
      font-size: 10px;
      line-height: 1.4;
    }
  }
`;

export const StatsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: clamp(30px, 5vw, 60px);
  margin-top: clamp(40px, 8vh, 60px);
  flex-wrap: wrap;

  @media screen and (max-width: 768px) {
    gap: 40px;
    margin-top: 48px;
  }

  @media screen and (max-width: 480px) {
    gap: 24px;
    margin-top: 32px;
  }
`;

export const StatItem = styled.div`
  text-align: center;
  cursor: default;
  transition: transform 0.3s ease;

  span {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 700;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    display: block;
    line-height: 1.2;
    margin-bottom: 8px;
  }

  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: clamp(0.75rem, 2vw, 0.9rem);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-top: 4px;
    font-weight: 500;
  }

  @media screen and (max-width: 768px) {
    span {
      font-size: clamp(1.8rem, 4vw, 2.5rem);
    }
    p {
      font-size: 0.8rem;
    }
  }

  @media screen and (max-width: 480px) {
    span {
      font-size: 1.6rem;
    }
    p {
      font-size: 0.7rem;
      letter-spacing: 0.5px;
    }
  }
`;

export const ScrollIndicator = styled(LinkScroll)`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }

  div {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media screen and (max-width: 768px) {
    bottom: 20px;
    font-size: 10px;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  @media screen and (max-width: 480px) {
    bottom: 15px;
    font-size: 9px;

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

export const ParticlesCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;

  @media screen and (max-width: 768px) {
    opacity: 0.5; /* Reduce opacity on mobile for better performance */
  }
`;
